/**
 * Funnel session + lead state.
 *
 * Mint failures (ad blocker, offline, backend down) MUST NOT throw through
 * render. `useEnsureFunnelSession` surfaces them as `error`; `updateLead`
 * lazy-mints so page load triggers no backend call.
 */

import {
  getIntakeGetMyLeadQueryKey,
  intakeCreateSession,
  useIntakeGetMyLead,
  useIntakeUpdateMyLead,
} from '@/api/generated-catch-backend/funnel/funnel';
import type {
  FunnelLeadResponse,
  UpdateFunnelLeadRequest,
} from '@/api/generated-catch-backend/model';
import {
  FUNNEL_LEAD_KEY,
  FUNNEL_SESSION_EXPIRES_KEY,
  FUNNEL_SESSION_EXPIRY_SKEW_MS,
  FUNNEL_SESSION_ID_KEY,
  FUNNEL_SESSION_TOKEN_KEY,
  type PositioningVariant,
} from '@/constants/funnel';
import sentryService from '@/services/sentry.service';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

interface UseFunnelLeadResult {
  lead: FunnelLeadResponse | undefined;
  isLoading: boolean;
  hasSession: boolean;
  updateLead: (update: UpdateFunnelLeadRequest) => Promise<FunnelLeadResponse>;
  isUpdating: boolean;
}

// Shared across callers so StrictMode double-mounts and parallel hook
// invocations don't race two POSTs. Cleared in `finally` so a retry isn't
// blocked by a settled-and-failed promise.
let inFlightMint: Promise<string> | null = null;

function reportMintFailure(error: unknown, positioningVariant?: PositioningVariant): void {
  const isNetworkError = axios.isAxiosError(error) && !error.response;
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  const code = axios.isAxiosError(error) ? error.code : undefined;

  sentryService.captureException(error as Error, {
    operation: 'mintFunnelSession',
    positioningVariant: positioningVariant ?? 'none',
    isNetworkError,
    status,
    errorCode: code,
    // Set when the request never reached the server — usually an ad blocker
    // or privacy tool. Lets us triage iOS Safari + AdGuard from a real 5xx
    // without a session replay.
    suspectedClientBlock: isNetworkError,
  });
}

async function mintFunnelSession(positioningVariant?: PositioningVariant): Promise<string> {
  // StrictMode double-mounts can land a second caller here after the first
  // already wrote to localStorage — re-read before queuing a new request.
  const existing = localStorage.getItem(FUNNEL_SESSION_TOKEN_KEY);
  if (existing && !isFunnelSessionNearExpiry()) return existing;

  if (inFlightMint) return inFlightMint;

  inFlightMint = (async () => {
    try {
      const response = await intakeCreateSession({
        positioningVariant: positioningVariant ?? null,
      });
      localStorage.setItem(FUNNEL_SESSION_TOKEN_KEY, response.funnelSessionToken);
      localStorage.setItem(FUNNEL_SESSION_ID_KEY, response.funnelSessionId);
      localStorage.setItem(FUNNEL_SESSION_EXPIRES_KEY, response.expiresAtUtc);
      return response.funnelSessionToken;
    } catch (error) {
      reportMintFailure(error, positioningVariant);
      throw error;
    } finally {
      inFlightMint = null;
    }
  })();

  return inFlightMint;
}

/**
 * Returns true if the stored token is unset, has no recorded expiry, or is
 * within FUNNEL_SESSION_EXPIRY_SKEW_MS of (or past) its server-side hard
 * expiry. A token that pre-dates this feature has no expiry recorded — we
 * treat that as "rotate proactively" so legacy tokens get refreshed on next
 * visit, rather than waiting for the 401 round-trip.
 */
function isFunnelSessionNearExpiry(): boolean {
  const token = localStorage.getItem(FUNNEL_SESSION_TOKEN_KEY);
  if (!token) return true;
  const raw = localStorage.getItem(FUNNEL_SESSION_EXPIRES_KEY);
  if (!raw) return true;
  const expiresAt = Date.parse(raw);
  if (Number.isNaN(expiresAt)) return true;
  return Date.now() >= expiresAt - FUNNEL_SESSION_EXPIRY_SKEW_MS;
}

async function ensureFunnelSession(positioningVariant?: PositioningVariant): Promise<string> {
  if (isFunnelSessionNearExpiry()) {
    clearFunnelSessionToken();
    return mintFunnelSession(positioningVariant);
  }
  const existing = localStorage.getItem(FUNNEL_SESSION_TOKEN_KEY);
  if (existing) return existing;
  return mintFunnelSession(positioningVariant);
}

export function getFunnelSessionToken(): string | null {
  return localStorage.getItem(FUNNEL_SESSION_TOKEN_KEY);
}

export function getFunnelSessionId(): string | null {
  return localStorage.getItem(FUNNEL_SESSION_ID_KEY);
}

export function clearFunnelSessionToken(): void {
  localStorage.removeItem(FUNNEL_SESSION_TOKEN_KEY);
  localStorage.removeItem(FUNNEL_SESSION_ID_KEY);
  localStorage.removeItem(FUNNEL_SESSION_EXPIRES_KEY);
  // FUNNEL_LEAD_KEY stays — it's the prefill source. A new session gets
  // minted around it, and the next field blur PATCHes the lead back into
  // the new server-side row.
}

/**
 * Minimum spacing between automatic re-mints triggered by a 401 from
 * `/v1/intake/session/me/lead`. If the backend hands out a token and
 * immediately 401s it, this prevents the recovery path from looping every
 * React Query retry. The user can still mint manually by reloading.
 */
const AUTO_REMINT_RATE_LIMIT_MS = 60 * 1000;
let lastAutoRemintAt = 0;

/**
 * Read the cached funnel lead synchronously from localStorage. Returns
 * `null` if no lead has been written yet (first-time visitor) or if the
 * stored payload is malformed.
 *
 * This is the local source of truth for funnel step components. They read
 * it once in their `useState` initializer to pre-fill fields, then drive
 * their own controlled inputs. The server's lead at
 * `/v1/intake/session/me/lead` is a durable mirror that doesn't round-trip
 * back into UI state — avoiding the hydration race where a freshly-minted
 * funnel session triggers a server fetch that overwrites whatever the user
 * just typed.
 */
export function getFunnelLead(): FunnelLeadResponse | null {
  const raw = localStorage.getItem(FUNNEL_LEAD_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as FunnelLeadResponse;
  } catch {
    return null;
  }
}

function setFunnelLead(lead: FunnelLeadResponse): void {
  try {
    localStorage.setItem(FUNNEL_LEAD_KEY, JSON.stringify(lead));
  } catch {
    // Quota exceeded or serialization failure — non-fatal. The mutation's
    // React Query cache still holds the latest lead; localStorage is just
    // a refresh-survival cache.
  }
}

/**
 * True if the server returned at least one non-empty user-typed field.
 * `updated_at_utc` is set whenever the row exists, so it doesn't count
 * as user-supplied data on its own; same for `positioning_variant`,
 * which is stamped at session mint time. The user-meaningful fields
 * are email, first_name, last_name, date_of_birth, biological_sex, state.
 */
function hasAnyLeadField(lead: FunnelLeadResponse): boolean {
  return Boolean(lead.email || lead.firstName || lead.lastName || lead.dateOfBirth || lead.biologicalSex || lead.state);
}

/**
 * Merge a server PATCH response into the existing lead, preferring the
 * server's value when present (so user edits propagate) and keeping
 * locally-known fields the server didn't return (so a rotated session
 * doesn't lose fields PATCHed under the prior session).
 */
function mergeLead(existing: FunnelLeadResponse, updated: FunnelLeadResponse): FunnelLeadResponse {
  return {
    email: updated.email ?? existing.email,
    firstName: updated.firstName ?? existing.firstName,
    lastName: updated.lastName ?? existing.lastName,
    dateOfBirth: updated.dateOfBirth ?? existing.dateOfBirth,
    biologicalSex: updated.biologicalSex ?? existing.biologicalSex,
    state: updated.state ?? existing.state,
    positioningVariant: updated.positioningVariant ?? existing.positioningVariant,
    updatedAtUtc: updated.updatedAtUtc ?? existing.updatedAtUtc,
  };
}

interface UseEnsureFunnelSessionResult {
  hasSession: boolean;
  isInitializing: boolean;
  error: Error | null;
  retry: () => void;
}

/**
 * Best-effort funnel session bootstrap. Callers MUST NOT gate render on
 * `hasSession` — render the funnel and show an error banner if `error` is
 * set. `positioningVariant` is ignored when a session already exists; the
 * variant was locked in at mint time.
 */
export function useEnsureFunnelSession(positioningVariant?: PositioningVariant): UseEnsureFunnelSessionResult {
  // Treat a near-expiry token the same as no token: the FunnelGuard mount
  // is the right moment to rotate, so the user's downstream blurs go to a
  // fresh session token without ever generating a 401 on /me/lead.
  const tokenIsLive = Boolean(getFunnelSessionToken()) && !isFunnelSessionNearExpiry();
  const [hasSession, setHasSession] = useState(tokenIsLive);
  const [isInitializing, setIsInitializing] = useState(!tokenIsLive);
  const [error, setError] = useState<Error | null>(null);
  // Bumping this re-fires the effect from a button handler.
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    if (hasSession) return;
    let cancelled = false;
    setIsInitializing(true);
    setError(null);
    void (async () => {
      try {
        await ensureFunnelSession(positioningVariant);
        if (!cancelled) {
          setHasSession(true);
          setError(null);
        }
      } catch (mintError) {
        if (!cancelled) {
          setError(mintError instanceof Error ? mintError : new Error(String(mintError)));
        }
      } finally {
        if (!cancelled) {
          setIsInitializing(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hasSession, positioningVariant, retryNonce]);

  const retry = useCallback(() => {
    setRetryNonce((n) => n + 1);
  }, []);

  return { hasSession, isInitializing, error, retry };
}

export function useFunnelLead(): UseFunnelLeadResult {
  const queryClient = useQueryClient();
  const hasSession = Boolean(getFunnelSessionToken());
  const recoveryInFlightRef = useRef(false);

  // Seed React Query from localStorage on first render so consumers
  // (PaymentPageV2 form pre-fill etc.) see the lead immediately, before
  // the server's GET resolves. Critical after a token rotation: the new
  // session's GET returns an empty FunnelLeadResponse, but localStorage
  // still has the user's email + name + DOB from before the rotation.
  // ``placeholderData`` is shown while the query is pending but doesn't
  // satisfy ``staleTime`` — the GET still fires on enable. The mirror
  // effect below only overwrites localStorage when the GET response
  // carries real fields, so the placeholder survives an empty server
  // response.
  const placeholderLeadRef = useRef<FunnelLeadResponse | null | undefined>(undefined);
  if (placeholderLeadRef.current === undefined) {
    placeholderLeadRef.current = getFunnelLead();
  }

  const { data: serverLead, isLoading, error: leadError } = useIntakeGetMyLead({
    query: {
      enabled: hasSession,
      // Lead changes are driven by our own PATCHes — don't refetch on focus.
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      placeholderData: placeholderLeadRef.current ?? undefined,
      // A 401 means the token is gone server-side, not a transient blip —
      // there is no value in retrying with the same dead token. The
      // recovery effect below handles the rotate-and-refetch.
      retry: (failureCount, error) => {
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          return false;
        }
        return failureCount < 3;
      },
    },
  });

  // What we expose to callers: a merge of the localStorage snapshot
  // (durable across sessions) and the server lead (most recent state
  // for the current session). The server's value wins where it has
  // one; localStorage fills in fields the new server-side session
  // doesn't have yet — typically fields PATCHed under a pre-rotation
  // session, like email after a near-expiry rotation.
  const lead: FunnelLeadResponse | undefined =
    serverLead || placeholderLeadRef.current
      ? mergeLead(placeholderLeadRef.current ?? {}, serverLead ?? {})
      : undefined;

  // Mirror the GET response to localStorage by MERGING with what is
  // already there, never replacing. Server values take precedence when
  // present, but a `null` field from the server (typical for a rotated
  // session that hasn't accumulated all the user's data yet) must not
  // clobber the value already cached locally. Without the merge, a GET
  // that returned the rotated session's partial lead would erase the
  // email that was PATCHed under the prior session.
  useEffect(() => {
    if (!serverLead) return;
    const stored = getFunnelLead() ?? {};
    setFunnelLead(mergeLead(stored, serverLead));
  }, [serverLead]);

  // 401 recovery: the backend told us the token is expired or revoked.
  // Clear it, mint a fresh session, invalidate the query so it refetches
  // with the new token. Rate-limited so a backend that 401s every call
  // (misconfigured secret, clock skew, persistent revocation) cannot
  // amplify into an infinite mint loop — the user falls through to the
  // existing FunnelGuard error banner if rotation can't recover.
  useEffect(() => {
    if (!leadError) return;
    if (!axios.isAxiosError(leadError) || leadError.response?.status !== 401) return;
    if (recoveryInFlightRef.current) return;
    const now = Date.now();
    if (now - lastAutoRemintAt < AUTO_REMINT_RATE_LIMIT_MS) return;

    recoveryInFlightRef.current = true;
    lastAutoRemintAt = now;
    void (async () => {
      try {
        clearFunnelSessionToken();
        await mintFunnelSession();
        await queryClient.invalidateQueries({ queryKey: getIntakeGetMyLeadQueryKey() });
      } catch (mintError) {
        sentryService.captureException(mintError as Error, {
          operation: 'funnelSessionAutoRotateOn401',
        });
      } finally {
        recoveryInFlightRef.current = false;
      }
    })();
  }, [leadError, queryClient]);

  const updateMutation = useIntakeUpdateMyLead({
    mutation: {
      onSuccess: (updated) => {
        // Merge the PATCH response into both the React Query cache and
        // localStorage rather than replacing. After a token rotation the
        // new session's row only has whichever fields the user has
        // PATCHed since the rotation; replacing would erase fields the
        // user filled in before the rotation (commonly: email).
        //
        // The cache may already hold an all-null FunnelLeadResponse from
        // the rotated session's GET, so we merge in BOTH the cache and
        // localStorage: cache first (most recent server state), then
        // localStorage (cross-session prefill source) filling any null
        // fields. The mergeLead helper drops nulls so localStorage values
        // survive when the cache's value for the same field is null.
        const cached = (queryClient.getQueryData(getIntakeGetMyLeadQueryKey()) as FunnelLeadResponse | undefined) ?? {};
        const stored = getFunnelLead() ?? {};
        const existing = mergeLead(stored, cached);
        const merged = mergeLead(existing, updated);
        queryClient.setQueryData(getIntakeGetMyLeadQueryKey(), merged);
        setFunnelLead(merged);
      },
    },
  });

  // Lazy-mint: nothing hits the backend until the user blurs their first
  // field. If mint fails, we never PATCH; the error propagates to the
  // caller's `catch`.
  const updateLead = useCallback(
    async (update: UpdateFunnelLeadRequest) => {
      await ensureFunnelSession();
      const result = await updateMutation.mutateAsync({ data: update });
      return result;
    },
    [updateMutation]
  );

  return {
    lead,
    isLoading: isLoading || !hasSession,
    hasSession,
    updateLead,
    isUpdating: updateMutation.isPending,
  };
}
